import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Reorder, useDragControls } from "framer-motion";
import { Copy, QrCode, Plus, X, Sparkles, AlertTriangle, Trash2, Mail, Send, GripVertical, Images } from "lucide-react";
import api, { apiError, fileUrl } from "@/lib/api";
import { fmtDateTime } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()));

function ComparePanel({ latest, prev }) {
  const prevRoom = (name) => (prev.rooms || []).find((r) => r.name === name);
  const rows = (latest.rooms || [])
    .map((rm) => ({ name: rm.name, cur: rm.photos || [], prev: (prevRoom(rm.name)?.photos) || [] }))
    .filter((x) => x.cur.length || x.prev.length);
  return (
    <div data-testid="photo-compare" className="border border-border rounded-md p-3 mb-4 bg-secondary/20">
      <p className="text-xs text-muted-foreground mb-3">Latest clean (<strong className="text-foreground">{fmtDateTime(latest.created_at)}</strong>) vs previous (<strong className="text-foreground">{fmtDateTime(prev.created_at)}</strong>) — spot wear, damage or missed spots.</p>
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">No room photos on these two cleans to compare.</p>
      ) : (
        <div className="space-y-3">
          {rows.map((row, i) => (
            <div key={i} data-testid={`compare-room-${i}`}>
              <p className="overline text-[0.6rem] mb-1.5">{row.name}</p>
              <div className="grid grid-cols-2 gap-3">
                {[["Latest", row.cur], ["Previous", row.prev]].map(([lbl, arr]) => (
                  <div key={lbl}>
                    <p className="text-[0.6rem] text-muted-foreground mb-1">{lbl}</p>
                    <div className="flex gap-1.5 flex-wrap">
                      {arr.length ? arr.map((ph, k) => <a key={k} href={fileUrl(ph)} target="_blank" rel="noreferrer"><img src={fileUrl(ph)} alt="" className="h-16 w-16 object-cover rounded-md border border-border" /></a>) : <span className="text-[0.6rem] text-muted-foreground">—</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RoomBlock({ room, onChange, onRemove }) {
  const controls = useDragControls();
  const [newItem, setNewItem] = useState("");
  const items = room.items || [];
  const addItem = () => { const v = newItem.trim(); if (v && !items.includes(v)) { onChange({ ...room, items: [...items, v] }); setNewItem(""); } };
  const removeItem = (i) => onChange({ ...room, items: items.filter((x) => x !== i) });
  return (
    <Reorder.Item value={room} dragListener={false} dragControls={controls} data-testid={`prop-room-${room.id}`} className="border border-border rounded-md p-3 space-y-2.5 bg-secondary/20 list-none">
      <div className="flex items-center gap-2">
        <span onPointerDown={(e) => controls.start(e)} data-testid={`drag-room-${room.id}`} className="cursor-grab active:cursor-grabbing touch-none text-muted-foreground shrink-0"><GripVertical className="h-4 w-4" /></span>
        <Input data-testid={`prop-room-name-${room.id}`} value={room.name} onChange={(e) => onChange({ ...room, name: e.target.value })} className="font-medium bg-white" placeholder="Room name" />
        <Button variant="ghost" size="icon" data-testid={`prop-del-room-${room.id}`} onClick={onRemove}><Trash2 className="h-4 w-4 text-[#9E382A]" /></Button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((i) => (
          <span key={i} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-[#2B4C3F]/12 text-[#2B4C3F]">{i}<button onClick={() => removeItem(i)}><X className="h-3 w-3" /></button></span>
        ))}
        {items.length === 0 && <span className="text-xs text-muted-foreground">No tasks in this room.</span>}
      </div>
      <div className="flex gap-2">
        <Input data-testid={`prop-room-item-input-${room.id}`} value={newItem} onChange={(e) => setNewItem(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addItem()} className="bg-white" placeholder="Add custom task…" />
        <Button variant="outline" size="sm" data-testid={`prop-add-room-item-${room.id}`} onClick={addItem}><Plus className="h-4 w-4" /></Button>
      </div>
    </Reorder.Item>
  );
}

export function CleaningCard({ propertyId, role }) {
  const canManage = ["admin", "operations", "compliance"].includes(role);
  const [data, setData] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [library, setLibrary] = useState([]);
  const [presets, setPresets] = useState([]);
  const [pick, setPick] = useState("");
  const [presetPick, setPresetPick] = useState("");
  const [showCompare, setShowCompare] = useState(false);
  const [cleaner, setCleaner] = useState({ cleaner_name: "", cleaner_email: "" });
  const [savedCleaners, setSavedCleaners] = useState([]);
  useEffect(() => { api.get("/cleaners").then((r) => setSavedCleaners(r.data)).catch(() => {}); }, []);

  const load = () => api.get(`/properties/${propertyId}/cleaning`).then((r) => {
    setData(r.data);
    setRooms((r.data.rooms || []).map((x) => ({ ...x, id: x.id || uid() })));
    setLibrary(r.data.room_library || []);
    setPresets(r.data.presets || []);
    setCleaner({ cleaner_name: r.data.cleaner_name || "", cleaner_email: r.data.cleaner_email || "" });
  }).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [propertyId]);

  if (!data) return <div className="overline text-muted-foreground">Loading…</div>;
  const url = `${window.location.origin}/clean/${data.token}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch { toast.error("Couldn't copy — select the link and copy it"); }
  };
  const addFromLibrary = (id) => {
    setPick("");
    if (id === "custom") { setRooms([...rooms, { id: uid(), name: "New room", items: [] }]); return; }
    const lib = library.find((l) => l.id === id);
    if (lib) setRooms([...rooms, { id: uid(), name: lib.name, items: [...(lib.items || [])] }]);
  };
  const applyPreset = (id) => {
    setPresetPick("");
    const p = presets.find((x) => x.id === id);
    if (!p) return;
    const add = [];
    (p.rooms || []).forEach((entry) => {
      const lib = library.find((l) => l.id === entry.room_id);
      if (!lib) return;
      const qty = Math.max(1, entry.qty || 1);
      for (let k = 0; k < qty; k++) add.push({ id: uid(), name: lib.name, items: [...(lib.items || [])] });
    });
    if (add.length) { setRooms([...rooms, ...add]); toast.success(`Added ${add.length} room${add.length > 1 ? "s" : ""} from "${p.name}" — remember to Save`); }
    else toast.error("This preset has no matching library rooms — check it in Settings.");
  };
  const updateRoom = (idx, r) => setRooms(rooms.map((x, i) => (i === idx ? r : x)));
  const removeRoom = (idx) => setRooms(rooms.filter((_, i) => i !== idx));
  const saveLayout = async () => {
    const clean = rooms.map((r) => ({ id: r.id, name: (r.name || "").trim(), items: (r.items || []).filter((x) => x && x.trim()) })).filter((r) => r.name && r.items.length);
    const dropped = rooms.length - clean.length;
    try { const { data: d } = await api.put(`/properties/${propertyId}/cleaning-layout`, { rooms: clean }); setRooms((d.rooms || []).map((x) => ({ ...x, id: x.id || uid() }))); toast.success(dropped > 0 ? `Cleaning layout saved (${dropped} empty room${dropped > 1 ? "s" : ""} skipped)` : "Cleaning layout saved"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveCleaner = async () => {
    try { await api.put(`/properties/${propertyId}`, { cleaner_name: cleaner.cleaner_name, cleaner_email: cleaner.cleaner_email }); toast.success("Cleaner saved"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const sendLink = async () => {
    try { const { data: d } = await api.post(`/properties/${propertyId}/send-cleaning-link`); toast.success(d.delivered ? `Cleaning link emailed to ${d.sent_to}` : `Link created for ${d.sent_to} (email not delivered)`); }
    catch (e) { toast.error(apiError(e)); }
  };
  const delLog = async (id) => {
    try { await api.delete(`/cleaning-logs/${id}`); toast.success("Log deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="cleaning-card">
      {canManage && (
        <div className="bg-white border border-border rounded-md p-5">
          <div className="flex items-start gap-5 flex-wrap">
            <div className="bg-white p-2 border border-border rounded-md"><QRCodeSVG value={url} size={120} /></div>
            <div className="flex-1 min-w-[220px]">
              <p className="overline flex items-center gap-1.5"><QrCode className="h-3.5 w-3.5" />Cleaner checklist link</p>
              <p className="text-sm text-muted-foreground mt-1">Print the QR for the property or share the link with your cleaners. They tick each task room-by-room, add notes, and only attach a photo if they're reporting a major issue — no login needed.</p>
              <div className="flex items-center gap-2 mt-3">
                <input readOnly value={url} data-testid="cleaning-url" className="flex-1 text-xs px-3 py-2 border border-border rounded-md bg-secondary/40" />
                <Button size="sm" variant="outline" data-testid="copy-cleaning-link" onClick={copy}><Copy className="h-3.5 w-3.5 mr-1" />Copy</Button>
              </div>
            </div>
          </div>

          <div className="mt-5 pt-4 border-t border-border">
            <p className="overline flex items-center gap-1.5 mb-2"><Mail className="h-3.5 w-3.5" />Cleaner contact</p>
            <p className="text-xs text-muted-foreground mb-2">Save your cleaner's details to email them this checklist link — manually, or automatically on each check-out day.</p>
            {savedCleaners.length > 0 && (
              <Select value="" onValueChange={(id) => { const c = savedCleaners.find((x) => x.id === id); if (c) setCleaner({ cleaner_name: c.name, cleaner_email: c.email || "" }); }}>
                <SelectTrigger data-testid="saved-cleaner-pick" className="mb-2 h-8 text-xs bg-secondary/40"><SelectValue placeholder="Pick a saved cleaner…" /></SelectTrigger>
                <SelectContent>{savedCleaners.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`saved-cleaner-option-${c.id}`}>{c.name}{c.email ? ` · ${c.email}` : ""}{c.company ? ` (${c.company})` : ""}</SelectItem>)}</SelectContent>
              </Select>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Input data-testid="cleaner-name-input" value={cleaner.cleaner_name} onChange={(e) => setCleaner({ ...cleaner, cleaner_name: e.target.value })} placeholder="Cleaner name" />
              <Input data-testid="cleaner-email-input" value={cleaner.cleaner_email} onChange={(e) => setCleaner({ ...cleaner, cleaner_email: e.target.value })} placeholder="Cleaner email" />
            </div>
            <div className="flex gap-2 mt-2">
              <Button size="sm" data-testid="save-cleaner-button" onClick={saveCleaner}>Save cleaner</Button>
              <Button size="sm" variant="outline" data-testid="send-cleaning-link-button" disabled={!cleaner.cleaner_email.trim()} onClick={sendLink}><Send className="h-3.5 w-3.5 mr-1" />Email link now</Button>
            </div>
          </div>

          <div className="mt-5 pt-4 border-t border-border">
            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
              <p className="overline flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" />Cleaning rooms & tasks</p>
              <div className="flex items-center gap-2">
                {presets.length > 0 && (
                  <Select value={presetPick} onValueChange={applyPreset}>
                    <SelectTrigger data-testid="apply-preset-select" className="w-[160px] h-9"><SelectValue placeholder="Apply preset…" /></SelectTrigger>
                    <SelectContent>
                      {presets.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                <Select value={pick} onValueChange={addFromLibrary}>
                  <SelectTrigger data-testid="add-room-select" className="w-[200px] h-9"><SelectValue placeholder="Add room from library…" /></SelectTrigger>
                  <SelectContent>
                    {library.map((l) => <SelectItem key={l.id} value={l.id}>{l.name} ({(l.items || []).length})</SelectItem>)}
                    <SelectItem value="custom">+ Custom room</SelectItem>
                  </SelectContent>
                </Select>
                <Button size="sm" data-testid="save-cleaning-layout" onClick={saveLayout}>Save</Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mb-3">Add rooms from your Settings library (add the same room twice for e.g. two bathrooms), drag the handle to set cleaning order, then rename or add custom tasks per property.</p>
            <Reorder.Group axis="y" values={rooms} onReorder={setRooms} className="space-y-3">
              {rooms.map((r, i) => (
                <RoomBlock key={r.id} room={r} onChange={(nr) => updateRoom(i, nr)} onRemove={() => removeRoom(i)} />
              ))}
            </Reorder.Group>
            {rooms.length === 0 && <p data-testid="no-rooms" className="text-sm text-muted-foreground py-4 text-center border border-dashed border-border rounded-md">No rooms yet — add one from the library above.</p>}
          </div>
        </div>
      )}

      <div className="bg-white border border-border rounded-md p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="overline">Cleaning history</p>
          {(data.logs || []).length >= 2 && (
            <Button size="sm" variant="outline" data-testid="toggle-compare" onClick={() => setShowCompare((v) => !v)}><Images className="h-3.5 w-3.5 mr-1" />{showCompare ? "Hide compare" : "Compare photos"}</Button>
          )}
        </div>
        {showCompare && (data.logs || []).length >= 2 && <ComparePanel latest={data.logs[0]} prev={data.logs[1]} />}
        {(data.logs || []).length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">No cleaning logged yet.</p>
        ) : (
          <div className="space-y-3">
            {data.logs.map((l) => (
              <div key={l.id} data-testid={`clean-log-${l.id}`} className="border border-border rounded-md p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold">{l.cleaner_name}{l.cleaner_company ? ` · ${l.cleaner_company}` : ""}</p>
                    <p className="text-xs text-muted-foreground">{fmtDateTime(l.created_at)} · {l.done_count}/{l.total_count} tasks done</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {l.has_issue && <span className="text-[0.6rem] px-2 py-0.5 rounded-full bg-[#D98324]/15 text-[#D98324] flex items-center gap-1"><AlertTriangle className="h-3 w-3" />Issue flagged</span>}
                    {canManage && <button data-testid={`del-clean-log-${l.id}`} onClick={() => delLog(l.id)}><Trash2 className="h-3.5 w-3.5 text-[#9E382A]" /></button>}
                  </div>
                </div>
                {(l.rooms && l.rooms.length > 0) ? (
                  <div className="mt-2 space-y-2">
                    {l.rooms.map((rm, ri) => (
                      <div key={ri}>
                        <p className="overline text-[0.6rem] mb-1">{rm.name}</p>
                        <div className="flex flex-wrap gap-1">
                          {rm.items.map((it, i) => <span key={i} className={`text-[0.6rem] px-2 py-0.5 rounded-full ${it.done ? "bg-[#2B4C3F]/12 text-[#2B4C3F]" : "bg-secondary text-muted-foreground line-through"}`}>{it.name}</span>)}
                        </div>
                        {rm.photos?.length > 0 && (
                          <div className="flex gap-2 flex-wrap mt-1.5">
                            {rm.photos.map((ph, pi) => <a key={pi} href={fileUrl(ph)} target="_blank" rel="noreferrer"><img src={fileUrl(ph)} alt="" className="h-14 w-14 object-cover rounded-md border border-border" /></a>)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : l.items?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {l.items.map((it, i) => <span key={i} className={`text-[0.6rem] px-2 py-0.5 rounded-full ${it.done ? "bg-[#2B4C3F]/12 text-[#2B4C3F]" : "bg-secondary text-muted-foreground line-through"}`}>{it.name}</span>)}
                  </div>
                )}
                {l.notes && <p className="text-xs mt-2 text-muted-foreground whitespace-pre-wrap"><span className="overline">Notes</span> {l.notes}</p>}
                {l.has_issue && l.issue_description && <p className="text-xs mt-1 text-[#9E382A] whitespace-pre-wrap"><span className="overline">Issue</span> {l.issue_description}</p>}
                {l.photos?.length > 0 && (
                  <div className="flex gap-2 flex-wrap mt-2">
                    {l.photos.map((ph, i) => <a key={i} href={fileUrl(ph)} target="_blank" rel="noreferrer"><img src={fileUrl(ph)} alt="" className="h-16 w-16 object-cover rounded-md border border-border" /></a>)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
